import React, { useRef, useState, useEffect } from 'react';
import { useThrottleFn } from 'ahooks';
import classnames from 'classnames';

import type { IBaseProps } from '@home/types/common';

import './index.scss';

// 固定底部滚动条的props
interface IFixedBottomScrollbarProps extends IBaseProps {
  // 初始滚动left值
  initialLeft?: number;
  // 重新计算宽度、滚动宽度的更新依赖
  deps: React.DependencyList;
  // 获取支持滚动交互那个dom
  getScrollableEl: () => HTMLElement | null;
  // 滚动条滚动的回调
  onScroll?: (e?: any) => void;
}

const FixedBottomScrollbar: React.FC<IFixedBottomScrollbarProps> = (props) => {
  const { style, className, initialLeft, deps, getScrollableEl, onScroll } = props;

  // 外部支持滚动那个dom
  const scrollableEl1 = useRef<HTMLElement | null>(null);
  // 固定底部这个滚动条dom
  const scrollableEl2 = useRef<HTMLDivElement | null>(null);

  // 是否展示滚动条
  const [visible, setVisible] = useState(false);
  // 容器宽度
  const [clientWidth, setClientWidth] = useState(0);
  // 滚动宽度
  const [scrollWidth, setScrollWidth] = useState(0);

  /**
   * 处理外部支持滚动dom的滚动事件
   * @param e 滚动事件对象
   */
  const handleScrollableEl1Scroll = (e?: any) => {
    if (!scrollableEl2.current) return;
    // scrollableEl2.current?.scrollTo({ left: e?.target?.scrollLeft, behavior: 'smooth' });
    scrollableEl2.current?.scrollTo({ left: e?.target?.scrollLeft });
  };

  /**
   * 处理固定底部滚动dom的滚动事件
   * @param e 滚动事件对象
   */
  const handleScrollableEl2Scroll = (e?: any) => {
    onScroll?.(e);
    if (!scrollableEl1.current) return;
    // scrollableEl1.current.scrollTo({ left: e?.target?.scrollLeft, behavior: 'smooth' });
    scrollableEl1.current.scrollTo({ left: e?.target?.scrollLeft });
  };

  const handleResize = () => {
    if (!scrollableEl1.current) return;
    const scrollableElClientWidth = scrollableEl1.current.clientWidth;
    const scrollableElScrollWidth = scrollableEl1.current.scrollWidth;
    const visibleTmp = scrollableElScrollWidth > scrollableElClientWidth;
    setVisible(visibleTmp);
    setClientWidth(scrollableElClientWidth);
    setScrollWidth(scrollableElScrollWidth);
  };

  const { run: handleResizeRun } = useThrottleFn(handleResize, {
    leading: true,
    trailing: true,
    wait: 50,
  });

  /**
   * 初始化
   * 1、计算是否溢出，需要显示滚动条
   * 2、设置容器宽度、容器的滚动宽度
   * 3、隐藏外部支持滚动dom的浏览器原生滚动条
   */
  useEffect(() => {
    setTimeout(() => {
      const scrollableElTmp = getScrollableEl();
      if (!scrollableElTmp) return;
      const scrollableElClientWidth = scrollableElTmp.clientWidth;
      const scrollableElScrollWidth = scrollableElTmp.scrollWidth;
      const visibleTmp = scrollableElScrollWidth > scrollableElClientWidth;
      scrollableEl1.current = scrollableElTmp;
      setVisible(visibleTmp);
      setClientWidth(scrollableElClientWidth);
      setScrollWidth(scrollableElScrollWidth);

      if (visibleTmp && typeof initialLeft === 'number' && !Number.isNaN(initialLeft)) {
        scrollableEl2.current?.scrollTo({ left: initialLeft });
      }
    });
  }, [deps]);

  /**
   * 初始化，绑定滚动事件，同步滚动距离到当前组件这个滚动条
   */
  useEffect(() => {
    const scrollableEl1 = getScrollableEl();
    if (!scrollableEl1) return;

    scrollableEl1.addEventListener('scroll', handleScrollableEl1Scroll, false);

    return () => {
      scrollableEl1.removeEventListener('scroll', handleScrollableEl1Scroll, false);
    };
  }, [deps]);

  /**
   * 初始化，绑定resize事件，从新计算clientWidth、scrollWidth，判定是否需要显示滚动条
   */
  useEffect(() => {
    window.addEventListener('resize', handleResizeRun, false);

    return () => {
      window.removeEventListener('resize', handleResizeRun, false);
    };
  }, [deps]);

  return (
    <>
      {visible && (
        <div
          ref={scrollableEl2}
          style={{ ...style, width: clientWidth }}
          className={classnames('dbh-fixed-bottom-scrollbar', className)}
          onScroll={handleScrollableEl2Scroll}
        >
          <div style={{ width: scrollWidth }} className="dbh-fixed-bottom-scrollbar__thumb"></div>
        </div>
      )}
    </>
  );
};

export default FixedBottomScrollbar;
